import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Global query retry policy (#1155 subitem 5, #2288).
 *
 * TanStack's default (`retry: 3`, exponential backoff) is wrong for a
 * REST admin console. A query is retried up to 2 times EXCEPT when the
 * failure is already known to be final, which happens on two independent
 * axes:
 *
 *   TRANSPORT — the request never got a verdict at all. `ApiError.transport`
 *   names which kind, and the two members are NOT treated alike: a
 *   `'timeout'` is terminal here because it already spent the whole request
 *   budget once (#2288), while a `'network'` failure (#2316) spent nothing
 *   and is the archetypal transient — a wifi blip on an admin console is
 *   exactly what a retry is for. So this axis excludes `'timeout'` only,
 *   and a third member would have to argue its own case rather than
 *   inherit either answer.
 *
 *   STATUS — the server answered, and answered terminally: a 4xx that is
 *   not 429 or 408. 403 admin revoked, 404 not found, 400 validation are
 *   not going to change on a second ask, and retrying just wastes ~7s
 *   before the operator sees the error.
 *
 * Everything else retries: 5xx, a bare `Error` with no `ApiError` at all,
 * and an `ApiError` with neither a terminal status nor a transport
 * failure. 429 (the backend rate-limit middleware emits it at 60/min with
 * a `Retry-After` header) and 408 are the two standard *transient* 4xx
 * codes and are deliberately excluded from the terminal band: a momentary
 * 429 self-heals, and treating the whole 4xx band as terminal would turn a
 * transient burst into a hard error the operator must re-trigger by hand.
 *
 * Note 408 is NOT the timeout the TRANSPORT axis is about. A 408 is a
 * status the server chose and sent; a client-side timeout has no response
 * and therefore no status at all (`api.ts` reads `status` from
 * `response?.status` and `transport` from the axios error code). The two
 * never describe the same failure, and stopping retries for one does not
 * contradict retrying the other.
 *
 * This is the single source for the policy — `useOrderTrace` used to carry
 * a per-hook special case and now inherits this instead. Do not restate
 * the rule elsewhere; point at this function.
 */
export function retryOnlyTransient(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    // The request never reached a verdict, and it burned the full budget
    // getting nowhere. `API_DEFAULT_TIMEOUT_MS` (see `api.ts`, which owns
    // the number and the prod percentile behind it) is sized by the failure
    // shape rather than the happy path, so a second and third budget buy
    // very little and cost the operator three timeouts' worth of spinner
    // before the `error.timeout` copy (#2289) may render at all.
    const timedOut = error.transport === 'timeout';

    // The server answered, and answered terminally. If a THIRD code ever
    // has to be excused here, stop extending this list: that is the signal
    // that retryability should be read from the response itself (a
    // `Retry-After` header, or a field in the error envelope) rather than
    // enumerated from status codes. Two exceptions is a band with two
    // holes; three is a lookup table wearing a boolean's clothes.
    const terminalClientError =
      typeof error.status === 'number' &&
      error.status >= 400 &&
      error.status < 500 &&
      error.status !== 429 &&
      error.status !== 408;

    if (timedOut || terminalClientError) {
      return false;
    }
  }
  return failureCount < 2;
}

/**
 * The single application-wide QueryClient.
 *
 * Exported as a module singleton (not just handed to
 * `QueryClientProvider`) so non-React code paths — specifically the
 * auth `signOut` and the 401 forced-logout handler in
 * `BetterAuthContext` — can call `queryClient.clear()` to purge every
 * cached query on logout (#1155 subitem 1). Without that purge, on a
 * shared device operator B could read operator A's cached lists /
 * details within the `staleTime` window.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryOnlyTransient,
    },
  },
});
